import { initData, isHosted } from "../platform/telegram";
import { FROSHBOT_API_URL, webId } from "../api/profile";

/**
 * Learn feed client — the first surface backed by the content engine instead
 * of mock services. Feed reads are public (item URLs render for anyone);
 * course selections ride the same dual-auth rail as the profile
 * (`tma <initData>` inside Telegram, `web <uuid>` in a browser).
 */

export interface CanonicalCourse {
  id: string;
  name: string;
  ready: boolean;
}

export type LearnFormat = "quiz" | "flashcards" | "mistake" | "pairs" | "sort" | "video";

export interface LearnItem {
  id: string;
  courseId: string;
  courseTopicId: string;
  topicTitle: string;
  format: LearnFormat;
  payload: Record<string, unknown>;
  sourceRef: string;
  syllabusWeek: number;
  createdAt: string;
}

export interface LearnFeedResponse {
  course: { id: string; name: string };
  items: LearnItem[];
}

function authHeader(): string | null {
  const data = initData();
  if (data) return `tma ${data}`;
  const web = webId();
  return web ? `web ${web}` : null;
}

export async function fetchLearnFeed(courseId: string): Promise<LearnFeedResponse | null> {
  try {
    const response = await fetch(`${FROSHBOT_API_URL}/feed/${encodeURIComponent(courseId)}`);
    if (!response.ok) return null;
    return (await response.json()) as LearnFeedResponse;
  } catch {
    return null;
  }
}

export interface CoursesState {
  catalog: CanonicalCourse[];
  selected: string[];
}

export async function fetchCourses(): Promise<CoursesState | null> {
  const auth = authHeader();
  // No identity (storage blocked): the picker still works, selection just
  // doesn't survive the session.
  if (!auth) return null;
  try {
    const response = await fetch(`${FROSHBOT_API_URL}/miniapp/courses`, {
      headers: { Authorization: auth },
    });
    if (!response.ok) return null;
    return (await response.json()) as CoursesState;
  } catch {
    return null;
  }
}

export async function saveCourses(selected: string[]): Promise<CoursesState | null> {
  const auth = authHeader();
  if (!auth) return null;
  try {
    const response = await fetch(`${FROSHBOT_API_URL}/miniapp/courses`, {
      method: "POST",
      headers: { Authorization: auth, "Content-Type": "application/json" },
      body: JSON.stringify({ courses: selected }),
    });
    if (!response.ok) return null;
    return (await response.json()) as CoursesState;
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ */
/* Browser deep links: /learn, /learn/<courseId>, /learn/<courseId>/<itemId> */
/* ------------------------------------------------------------------ */

export interface LearnPath {
  courseId: string | null;
  itemId: string | null;
}

export function parseLearnPath(pathname: string): LearnPath | null {
  const m = pathname.match(/^\/learn(?:\/([a-z0-9-]+))?(?:\/([a-z0-9-]+--[a-z]+))?\/?$/);
  if (!m) return null;
  return { courseId: m[1] ?? null, itemId: m[2] ?? null };
}

/** Reflect feed position into the address bar so every item is a permanent,
 * shareable URL — the atom of distribution (Distribution §1). No-op inside
 * Telegram, where there is no address bar to own. */
export function syncLearnUrl(courseId: string | null, itemId: string | null): void {
  if (isHosted) return;
  const path = courseId
    ? itemId
      ? `/learn/${courseId}/${itemId}`
      : `/learn/${courseId}`
    : "/learn";
  if (window.location.pathname !== path) {
    window.history.replaceState(null, "", path);
  }
}

export function itemShareUrl(courseId: string, itemId: string): string {
  return `https://join.freshman.app/learn/${courseId}/${itemId}`;
}
